import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Chip from '../../components/Chip';
import { CveText } from '../../components/CveLink';
import EmptyState from '../../components/EmptyState';
import Icon from '../../components/Icon';
import { EntityIcon, NodeState } from './ExplorerShared';
import { bomAffectedProducts, bomVulnerabilities, classLabel, matchesNode, recordSheet } from './explorerModel';
import type { GraphModel } from './graphModel';
import type { SimNode } from './simulation';

interface Props {
  model: GraphModel;
  rootId: string;
  /** Plural class names from the API, for list titles. */
  plurals: Readonly<Record<string, string>>;
  onSelect: (id: string) => void;
  onRootChange: (id: string) => void;
}

/** "PressFit 200 v1.0" under PressFit 200 reads "v1.0". */
function shortVersion(version: SimNode, product: string): string {
  return version.label.startsWith(`${product} `) ? version.label.slice(product.length + 1) : version.label;
}

/** Entries a list shows before "Alle anzeigen". */
const LIST_PREVIEW = 6;
/** From this many listed records on, the sheet offers a filter. */
const SEARCH_FROM = 12;

/**
 * Record sheet: the current record with every connection at once — single
 * references as fields, records pointing here as lists. Following a link makes
 * that record the current one and extends the path above; the path is the way
 * back.
 */
export default function RelationView({ model, rootId, plurals, onSelect, onRootChange }: Props): ReactNode {
  const [path, setPath] = useState<string[]>([rootId]);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const trailRef = useRef<HTMLOListElement>(null);
  // Reconcile a deleted record after a refresh; a valid path never contains a gap.
  const validPath = useMemo(() => {
    const missing = path.findIndex((id, index) => !model.byId.has(id)
      || (index > 0 && !model.neighbours.get(path[index - 1]!)?.has(id)));
    return missing < 0 ? path : path.slice(0, missing);
  }, [path, model]);
  const activeId = validPath.at(-1) ?? rootId;
  const previousId = validPath.length > 1 ? validPath.at(-2)! : null;
  const active = model.byId.get(activeId);
  const sheet = useMemo(() => recordSheet(model, activeId, plurals, previousId), [model, activeId, plurals, previousId]);
  const listed = sheet.lists.reduce((sum, list) => sum + list.nodes.length, 0);
  const vulnerabilities = useMemo(() => bomVulnerabilities(model, activeId), [model, activeId]);
  const affectedProducts = useMemo(() => bomAffectedProducts(model, activeId), [model, activeId]);

  useEffect(() => {
    const trail = trailRef.current;
    if (trail) trail.scrollTo({ left: trail.scrollWidth, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }, [activeId]);

  function open(id: string): void {
    const existing = validPath.indexOf(id);
    setPath(existing < 0 ? [...validPath, id] : validPath.slice(0, existing + 1));
    setQuery(''); setExpanded(new Set());
    onSelect(id);
  }

  if (!active) return null;

  const recordLink = (node: SimNode): ReactNode => (
    <button type="button" className="sheet-link" onClick={() => open(node.id)} title={`${classLabel(node.klass)} öffnen`}>
      <span className="sheet-link-label">{node.label}</span>
      <Icon name="chevron-right" size={14} />
    </button>
  );

  return <div className="relation-view">
    {/* The path only exists once the user has followed a connection; until then
        the starting point above is the whole story. Earlier steps jump back. */}
    {validPath.length > 1 ? <nav className="relation-crumbs" aria-label="Pfad">
      <ol ref={trailRef}>
        {validPath.map((id, index) => {
          const node = model.byId.get(id);
          if (!node) return null;
          const current = id === activeId;
          return <li key={id}>
            {index > 0 ? <Icon name="chevron-right" size={14} className="relation-crumb-sep" /> : null}
            {current
              ? <span className="relation-crumb is-current" aria-current="location" title={`${classLabel(node.klass)}: ${node.label}`}>{node.label}</span>
              : <button type="button" className="relation-crumb" onClick={() => open(id)} title={`Zurück zu ${classLabel(node.klass)}: ${node.label}`}>{node.label}</button>}
          </li>;
        })}
      </ol>
    </nav> : null}

    {/* The starting point is already named in the picker above, so its sheet
        opens with just its detail and the way into the master data. */}
    {activeId === rootId ? <div className="sheet-meta">
      {active.detail ? <span className="sheet-detail"><CveText text={active.detail} /></span> : <span />}
      <NodeState state={active.state} />
      <Link to={`/stammdaten?klasse=${encodeURIComponent(active.klass)}&id=${active.cardId}`}>In Stammdaten öffnen <Icon name="external" size={12} /></Link>
    </div> : <header className="sheet-head">
      <EntityIcon klass={active.klass} />
      <div className="sheet-title">
        <span className="sheet-class">{classLabel(active.klass)}</span>
        <h2><CveText text={active.label} /></h2>
        {active.detail ? <span className="sheet-detail"><CveText text={active.detail} /></span> : null}
      </div>
      <NodeState state={active.state} />
      <div className="sheet-actions">
        <button type="button" className="small" onClick={() => onRootChange(activeId)}>Als Ausgangspunkt</button>
        <Link to={`/stammdaten?klasse=${encodeURIComponent(active.klass)}&id=${active.cardId}`}>In Stammdaten öffnen <Icon name="external" size={12} /></Link>
      </div>
    </header>}

    {sheet.fields.length === 0 && sheet.lists.length === 0
      ? <EmptyState compact title="Keine weiteren Verknüpfungen">{previousId ? 'Über den Pfad geht es zurück.' : 'Für diesen Datensatz sind keine Beziehungen hinterlegt.'}</EmptyState>
      : null}

    {sheet.fields.length ? <dl className="sheet-fields">
      {sheet.fields.map((field) => <div key={field.label}>
        <dt>{field.label}</dt>
        <dd>{recordLink(field.node)}<NodeState state={field.node.state} /></dd>
      </div>)}
    </dl> : null}

    {/* Derived, not stored: walked through versions and bill of materials, so
        it is always current and names where a CVE comes in. */}
    {active.klass === 'ProduktType' || active.klass === 'ProduktVersion' ? <section className="sheet-list" aria-label="Schwachstellen über die Stückliste">
      <h3>Schwachstellen über die Stückliste<Chip>{vulnerabilities.length}</Chip></h3>
      {vulnerabilities.length ? <ul>
        {vulnerabilities.map(({ cve, components, versions }) => {
          const product = active.klass === 'ProduktType' ? active.label : '';
          const meta = [
            components.map((component) => component.label).join(', '),
            product ? `in ${versions.map((version) => shortVersion(version, product)).join(', ')}` : '',
          ].filter(Boolean).join(' · ');
          return <li key={cve.id}>
            {recordLink(cve)}
            <span className="sheet-row-detail">{meta}</span>
            <NodeState state={cve.state} />
          </li>;
        })}
      </ul> : <p className="sheet-empty">Keine Komponente {active.klass === 'ProduktType' ? 'einer Version' : 'dieser Version'} ist von einer erfassten CVE betroffen.</p>}
    </section> : null}

    {active.klass === 'CVE' ? <section className="sheet-list" aria-label="Betroffene Produkte über die Stückliste">
      <h3>Betroffene Produkte über die Stückliste<Chip>{affectedProducts.length}</Chip></h3>
      {affectedProducts.length ? <ul>
        {affectedProducts.map(({ product, versions }) => <li key={product.id}>
          {recordLink(product)}
          <span className="sheet-row-detail">{versions.length === 1 ? 'Version' : 'Versionen'} {versions.map((version) => shortVersion(version, product.label)).join(', ')}</span>
        </li>)}
      </ul> : <p className="sheet-empty">Kein Produkt enthält eine betroffene Komponentenversion.</p>}
    </section> : null}

    {listed >= SEARCH_FROM ? <label className="explorer-search sheet-search"><Icon name="search" /><input type="search" aria-label="Verknüpfte Datensätze durchsuchen" placeholder="Verknüpfte Datensätze durchsuchen …" value={query} onChange={(event) => setQuery(event.target.value)} /></label> : null}

    {sheet.lists.map((list) => {
      const nodes = list.nodes.filter((node) => matchesNode(node, query));
      if (query && nodes.length === 0) return null;
      const all = expanded.has(list.label);
      const shown = all ? nodes : nodes.slice(0, LIST_PREVIEW);
      return <section className="sheet-list" key={list.label} aria-label={list.label}>
        <h3>{list.label}<Chip>{nodes.length}</Chip></h3>
        <ul>
          {shown.map((node) => {
            // The owner tells look-alikes apart (one notice per customer), but
            // not where it only names the record this sheet is about.
            const context = node.context && !node.context.includes(active.label) ? node.context : '';
            const meta = [context, node.detail].filter(Boolean).join(' · ');
            return <li key={node.id}>
            {recordLink(node)}
            {meta ? <span className="sheet-row-detail">{meta}</span> : null}
            <NodeState state={node.state} />
          </li>;
          })}
        </ul>
        {nodes.length > LIST_PREVIEW ? <button type="button" className="link sheet-more" onClick={() => setExpanded((current) => {
          const next = new Set(current);
          if (all) next.delete(list.label); else next.add(list.label);
          return next;
        })}>{all ? 'Weniger anzeigen' : `Alle ${nodes.length} anzeigen`}</button> : null}
      </section>;
    })}
  </div>;
}
